/**
=========================================================
* Trust Cargo - Customer supplier report
=========================================================

* The suppliers system read from the customer's side.
*
* The Suppliers page answers "what do we owe this supplier". This one answers
* the question the office actually asks: this customer ordered from ten
* suppliers - who are they, where has each shipment got to, and what have we
* paid against it.
*
* Two ledgers meet here and they are deliberately kept apart:
*   - the customer's account with US (what they were charged, what they paid)
*   - what WE owe their suppliers in China
* Adding one to the other would be meaningless, so they are never summed.
*/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import SupplierPaymentDialog from "components/SupplierPaymentDialog";
import RowActions from "components/RowActions";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** One figure with its label - used for both ledgers' summaries. */
function Stat({ label, value, tone }) {
  return (
    <SuiBox
      px={2}
      py={1.25}
      sx={({ palette, borders, functions: { rgba } }) => ({
        borderRadius: borders.borderRadius.md,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        backgroundColor: rgba(palette[tone || "dark"].main, 0.05),
        minWidth: 130,
      })}
    >
      <SuiTypography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
        {label}
      </SuiTypography>
      <SuiTypography
        variant="h6"
        sx={({ palette }) => ({ color: palette[tone || "dark"].main, fontWeight: 700 })}
      >
        {value}
      </SuiTypography>
    </SuiBox>
  );
}

Stat.defaultProps = { tone: "dark" };

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

function CustomerSuppliers() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // the customer being drilled into, and their detail payload
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // {supplier_id, supplier_order_id, ...} when paying, or null when closed
  const [paying, setPaying] = useState(null);
  // supplier ids whose payment history is expanded
  const [showPayments, setShowPayments] = useState({});
  const [owing, setOwing] = useState("");
  const [goods, setGoods] = useState("");

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/customer-suppliers`, { cache: "no-store" });
      if (!res.ok) {
        setError(`Report unavailable (HTTP ${res.status})`);
        setRows([]);
      } else {
        setRows(await res.json());
      }
    } catch (e) {
      setError(e?.message || "Network error");
    }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/customer-suppliers/${id}`, { cache: "no-store" });
      setDetail(res.ok ? await res.json() : null);
    } catch (e) {
      setDetail(null);
    }
    setDetailLoading(false);
  };

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (openId) fetchDetail(openId);
    else setDetail(null);
  }, [openId]);

  /** After recording a payment both views are stale. */
  const refresh = async () => {
    await fetchList();
    if (openId) await fetchDetail(openId);
  };

  /**
   * Open the payment dialog. Everything a "pay" button already knows is
   * passed through, so paying one purchase is one click and a Save rather
   * than re-picking the supplier and the invoice by hand.
   */
  const openPayment = (extra) =>
    setPaying({ paid_at: new Date().toISOString().slice(0, 10), rate: 1, ...extra });

  const narrowed = rows.filter((r) => {
    if (owing === "owed" && !(Number(r.balance) > 0)) return false;
    if (owing === "settled" && Number(r.balance) > 0) return false;
    if (goods === "transit" && !(Number(r.in_transit) > 0)) return false;
    if (goods === "arrived" && !(Number(r.at_warehouse) > 0)) return false;
    return true;
  });

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const owed = filteredRows.reduce((s, r) => s + (Number(r.balance) || 0), 0);
  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  /* ------------------------------------------------------------------ */
  /* the list of customers                                               */
  /* ------------------------------------------------------------------ */

  const list = (
    <Card>
      <FilterBar
        title="Customer Suppliers"
        value={query}
        onChange={setQuery}
        count={filteredRows.length}
        total={rows.length}
        placeholder="Search by customer or code..."
        actions={
          <Button variant="contained" color="primary" onClick={() => openPayment({})}>
            Record Payment
          </Button>
        }
      >
        <FilterSelect
          label="Balance"
          icon="payments"
          value={owing}
          onChange={setOwing}
          anyLabel="All"
          options={[
            { value: "owed", label: "Still owed" },
            { value: "settled", label: "Settled" },
          ]}
        />
        <FilterSelect
          label="Goods"
          icon="inventory_2"
          value={goods}
          onChange={setGoods}
          anyLabel="All"
          options={[
            { value: "transit", label: "Some in transit" },
            { value: "arrived", label: "Some at warehouse" },
          ]}
        />

        <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
          {`Owed to suppliers ${money(owed)}`}
        </SuiTypography>
      </FilterBar>

      {error && (
        <SuiBox px={3} pb={2}>
          <Alert severity="error">{error}</Alert>
        </SuiBox>
      )}

      {loading ? (
        <CargoLoader label="Building report" />
      ) : (
        <>
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <IdHead />
                  <TableCell align="center">Code</TableCell>
                  <TableCell align="center">Customer</TableCell>
                  <TableCell align="center">Suppliers</TableCell>
                  <TableCell align="center">Purchases</TableCell>
                  <TableCell align="center">At warehouse</TableCell>
                  <TableCell align="center">In transit</TableCell>
                  <TableCell align="center">Invoiced</TableCell>
                  <TableCell align="center">Paid</TableCell>
                  <TableCell align="center">Balance</TableCell>
                  <TableCell align="center">Operations</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pager.pageRows.map((r) => (
                  <TableRow key={r.customer_id}>
                    <IdCell value={r.customer_id} />
                    <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                      {r.code ?? "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {r.customer ?? "-"}
                    </TableCell>

                    <TableCell align="center" sx={cell}>
                      <Chip
                        label={r.suppliers}
                        size="small"
                        sx={({ palette, functions: { rgba } }) => ({
                          fontWeight: 700,
                          backgroundColor: rgba(palette.primary.main, 0.16),
                          color: palette.dark.main,
                        })}
                      />
                    </TableCell>

                    <TableCell align="center" sx={cell}>
                      {r.purchases}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {r.at_warehouse}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {r.in_transit}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {money(r.invoiced)}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {money(r.paid)}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={({ palette }) => ({
                        ...cell,
                        fontWeight: 700,
                        color: Number(r.balance) > 0 ? palette.error.main : palette.success.main,
                      })}
                    >
                      {money(r.balance)}
                    </TableCell>

                    <TableCell align="center" sx={cell}>
                      <RowActions
                        actions={[
                          {
                            icon: "visibility",
                            label: "See all details",
                            tone: "info",
                            onClick: () => setOpenId(r.customer_id),
                          },
                          {
                            icon: "payments",
                            label: "Record a payment",
                            tone: "primary",
                            onClick: () => openPayment({}),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {filteredRows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <SuiTypography variant="button" color="text">
                        No customer buys from a supplier yet
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
          <Pagination {...pager} />
        </>
      )}
    </Card>
  );

  /* ------------------------------------------------------------------ */
  /* one customer in full                                                */
  /* ------------------------------------------------------------------ */

  const renderSupplier = (s) => (
    <Card key={s.supplier_id} sx={{ mb: 2.5 }}>
      {/* who they are, and their account for THIS customer */}
      <SuiBox
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        px={3}
        py={2}
        sx={({ palette, functions: { rgba } }) => ({
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
        })}
      >
        <SuiTypography variant="h6" sx={{ whiteSpace: "nowrap" }}>
          {s.code ? `${s.code} - ${s.name}` : s.name}
        </SuiTypography>

        {s.name_cn && (
          <SuiTypography variant="caption" sx={{ opacity: 0.7 }}>
            {s.name_cn}
          </SuiTypography>
        )}

        {[s.city, s.phone, s.wechat && `WeChat ${s.wechat}`].filter(Boolean).map((bit) => (
          <Chip key={bit} label={bit} size="small" variant="outlined" />
        ))}

        <SuiBox flex="1 1 auto" display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
          <Stat label="Invoiced" value={money(s.invoiced)} />
          <Stat label="Paid" value={money(s.paid)} tone="success" />
          <Stat
            label="Balance"
            value={money(s.balance)}
            tone={Number(s.balance) > 0 ? "error" : "success"}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => openPayment({ supplier_id: s.supplier_id })}
          >
            Pay supplier
          </Button>
        </SuiBox>
      </SuiBox>

      {/* money paid to this supplier that names no purchase cannot be
          credited to any customer - saying so beats silently under-reporting */}
      {s.unallocated > 0 && (
        <SuiBox px={3} pt={2}>
          <Alert severity="info">
            {`${money(
              s.unallocated
            )} was paid to this supplier without naming a purchase, so it is not counted
              against any customer. Edit those payments to attach an invoice.`}
          </Alert>
        </SuiBox>
      )}

      <SuiBox sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">Invoice</TableCell>
              <TableCell align="center">Goods</TableCell>
              <TableCell align="center">Shop code</TableCell>
              <TableCell align="center">Tracking</TableCell>
              <TableCell align="center">CTN / CBM / KG</TableCell>
              <TableCell align="center">Stage</TableCell>
              <TableCell align="center">Amount</TableCell>
              <TableCell align="center">Paid</TableCell>
              <TableCell align="center">Balance</TableCell>
              <TableCell align="center">Operations</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {s.purchases.map((p) => (
              <TableRow key={p.id}>
                <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                  {p.invoice_no || "-"}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 14 }}>
                  {p.description || "-"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {p.shop_code || "-"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {p.tracking_no || "-"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {`${p.ctn ?? "-"} / ${p.cbm || "-"} / ${p.weight_kg || "-"}`}
                </TableCell>

                <TableCell align="center" sx={{ ...cell, minWidth: 150 }}>
                  <Tooltip
                    arrow
                    title={
                      [
                        p.ordered_at && `Ordered ${p.ordered_at}`,
                        p.paid_at && `Paid ${p.paid_at}`,
                        p.shipped_at && `Shipped ${p.shipped_at}`,
                        p.received_at && `At warehouse ${p.received_at}`,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Not started"
                    }
                  >
                    <SuiBox>
                      <SuiTypography variant="caption" fontWeight="bold">
                        {p.stage}
                      </SuiTypography>
                      <LinearProgress
                        variant="determinate"
                        value={p.progress}
                        sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                      />
                    </SuiBox>
                  </Tooltip>
                </TableCell>

                <TableCell align="center" sx={cell}>
                  {`${money(p.amount)} ${p.currency || ""}`}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {money(p.paid)}
                </TableCell>

                <TableCell
                  align="center"
                  sx={({ palette }) => ({
                    ...cell,
                    fontWeight: 700,
                    color: Number(p.balance) > 0 ? palette.error.main : palette.success.main,
                  })}
                >
                  {money(p.balance)}
                </TableCell>

                <TableCell align="center" sx={cell}>
                  <RowActions
                    actions={[
                      {
                        icon: "payments",
                        label: "Pay this purchase",
                        tone: "primary",
                        onClick: () =>
                          openPayment({
                            supplier_id: s.supplier_id,
                            supplier_order_id: p.id,
                            amount: p.balance > 0 ? p.balance : undefined,
                            currency: p.currency || undefined,
                          }),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SuiBox>

      {/* the payments already made against this supplier's purchases */}
      <SuiBox px={3} py={2}>
        <Button
          size="small"
          onClick={() =>
            setShowPayments((prev) => ({ ...prev, [s.supplier_id]: !prev[s.supplier_id] }))
          }
          startIcon={<Icon>{showPayments[s.supplier_id] ? "expand_less" : "expand_more"}</Icon>}
        >
          {`${showPayments[s.supplier_id] ? "Hide" : "View"} payments (${s.payments.length})`}
        </Button>

        {showPayments[s.supplier_id] && (
          <SuiBox mt={1} sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Date</TableCell>
                  <TableCell align="center">Against invoice</TableCell>
                  <TableCell align="center">Method</TableCell>
                  <TableCell align="center">Reference</TableCell>
                  <TableCell align="center">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {s.payments.map((pay) => (
                  <TableRow key={pay.id}>
                    <TableCell align="center" sx={cell}>
                      {pay.paid_at || "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {pay.invoice_no || `#${pay.purchase_id}`}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {pay.method || "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {pay.reference || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                      {`${money(pay.amount)} ${pay.currency || ""}`}
                    </TableCell>
                  </TableRow>
                ))}

                {s.payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                      <SuiTypography variant="caption" color="text">
                        Nothing paid against this supplier for this customer yet
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );

  const detailView = detail && (
    <>
      <Card sx={{ mb: 2.5 }}>
        <SuiBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={3} py={2}>
          <Button onClick={() => setOpenId(null)} startIcon={<Icon>arrow_back</Icon>}>
            All customers
          </Button>

          <SuiTypography variant="h6" sx={{ whiteSpace: "nowrap" }}>
            {detail.customer.code
              ? `${detail.customer.code} - ${detail.customer.name}`
              : detail.customer.name}
          </SuiTypography>

          {detail.customer.mobile && (
            <Chip label={detail.customer.mobile} size="small" variant="outlined" />
          )}

          <SuiBox flex="1 1 auto" display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="contained" color="primary" onClick={() => openPayment({})}>
              Record Payment
            </Button>
          </SuiBox>
        </SuiBox>

        <Divider sx={{ my: 0 }} />

        {/* two separate ledgers, never added together */}
        <SuiBox px={3} py={2} display="flex" gap={4} flexWrap="wrap">
          <SuiBox>
            <SuiTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.7 }}>
              CUSTOMER ACCOUNT (what they owe us)
            </SuiTypography>
            <SuiBox display="flex" gap={1.5} mt={1} flexWrap="wrap">
              <Stat label="Orders" value={detail.account.orders} />
              <Stat label="Charged" value={money(detail.account.charged)} />
              <Stat label="Paid" value={money(detail.account.paid)} tone="success" />
              <Stat
                label="Balance"
                value={money(detail.account.balance)}
                tone={Number(detail.account.balance) > 0 ? "error" : "success"}
              />
            </SuiBox>
          </SuiBox>

          <SuiBox>
            <SuiTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.7 }}>
              CHINA SUPPLIERS (what we owe them)
            </SuiTypography>
            <SuiBox display="flex" gap={1.5} mt={1} flexWrap="wrap">
              <Stat label="Suppliers" value={detail.totals.suppliers} />
              <Stat label="Purchases" value={detail.totals.purchases} />
              <Stat label="Invoiced" value={money(detail.totals.invoiced)} />
              <Stat label="Paid" value={money(detail.totals.paid)} tone="success" />
              <Stat
                label="Balance"
                value={money(detail.totals.balance)}
                tone={Number(detail.totals.balance) > 0 ? "error" : "success"}
              />
            </SuiBox>
          </SuiBox>
        </SuiBox>

        {/* where the goods are, at a glance */}
        <SuiBox px={3} pb={2.5} display="flex" gap={1} flexWrap="wrap">
          {Object.entries(detail.stages)
            .filter(([, n]) => n > 0)
            .map(([label, n]) => (
              <Chip
                key={label}
                label={`${label}: ${n}`}
                size="small"
                sx={({ palette, functions: { rgba } }) => ({
                  fontWeight: 700,
                  backgroundColor: rgba(
                    label === "At warehouse" ? palette.success.main : palette.primary.main,
                    0.16
                  ),
                  color: palette.dark.main,
                })}
              />
            ))}
        </SuiBox>
      </Card>

      {detail.suppliers.map(renderSupplier)}

      {detail.suppliers.length === 0 && (
        <Card>
          <SuiBox p={4} textAlign="center">
            <SuiTypography variant="button" color="text">
              This customer has no supplier purchases recorded
            </SuiTypography>
          </SuiBox>
        </Card>
      )}
    </>
  );

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {openId ? detailLoading ? <CargoLoader label="Loading customer" /> : detailView : list}
      </SuiBox>

      {/* one dialog for every "pay" button - the defaults decide what it
          opens already pointed at */}
      <SupplierPaymentDialog
        open={Boolean(paying)}
        supplierId={paying?.supplier_id}
        onClose={() => setPaying(null)}
        onSaved={refresh}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default CustomerSuppliers;
